/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import { FaFileDownload } from 'react-icons/fa';
import api from '../../../services/api';

import { formatPrice, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';
import { usePerfil } from '../../../hooks/perfil';
import { useMedico } from '../../../hooks/medico';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import SelectSync, { IOption } from '../../../components/SelectSync';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import { arrayGroupBy } from '../../../utils/funcoes';

import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';
import HeaderRelatorio from '../HeaderRelatorio';
import SelectAsync from '../../../components/SelectAsync';

interface IRelatorio {
  AgeCodigo: number;
  AgeTipo: string;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  AgeStatus: number;
  AgeClinica: number;
  AgeConvCodigo: number;
  AgeConvNome: string;
  AgePlano: number;
  PacNome: string;
  Procedimento: string;
  ProCodigo: number;
  ProValor: number;
  ProValorFormatado: string;
  ConCodigo: number;
  ConNome: string;
  ConUsuCodigo: number;
  PerCodigo: number;
  CprClinica: number;
  CprValor: number;
  CprValorPag: number;
  CprValorFormatado: string;
  CprValorPagFormatado: string;
}

interface IRelatorioFormatado {
  grupo: string;
  totalDesconto: number;
  totalDescontoFormatado: string;
  total: number;
  totalFormatado: string;
  totalPago: number;
  totalPagoFormatado: string;
  dados: IRelatorio[];
}

interface ITotais {
  totalDescontoFormatado: string;
  totalFormatado: string;
  totalPagoFormatado: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
  PerCodigo: string;
  ConUsuCodigo: string;
  ProCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const FechamentoDia: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user, clinica } = useAuth();
  const { optionsClinica, convenios, buscaConvenios } = useClinica();

  const { optionsPerfil, buscaPerfil } = usePerfil();
  const { optionsMedico, buscaOptionsMedico } = useMedico();

  const [optionPerfil, setOptionsPerfil] = useState<IOption[]>([]);

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorioFormatado[]>();
  const [totais, setTotais] = useState<ITotais>();
  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [clinicaSelecionada, setClinicaSelecionada] = useState('');

  const exibeTotais =
    (clinica.CliDrSaude && user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR) ||
    (!clinica.CliDrSaude && user.UsuTipo <= TIPO_USUARIO.SUPERVISOR);

  const informacoesPagina = `<b>Descrição:</b> Exibe todos os procedimentos realizados em um determinado período, com o valor cobrado, <b>sem desconto</b>, o desconto é sobre o valor total, por isso só é contabilizado no somatório.
  ${
    clinica.CliDrSaude &&
    '<br /><b>Totais só são exibidos para ADMINISTRADORES</b>'
  }
  `;

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  useEffect(() => {
    async function loadMedicos(): Promise<void> {
      await buscaOptionsMedico();
    }
    if (!optionsMedico || Object.keys(optionsMedico).length === 0)
      loadMedicos();
  }, [buscaOptionsMedico, optionsMedico]);

  useEffect(() => {
    try {
      setOptionsPerfil(optionsPerfil.optionPerfis);
    } catch (error) {
      addToast({
        title: 'Não foi possível buscar perfis',
        type: 'error',
      });
    }
  }, [addToast, optionsPerfil]);

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  useEffect(() => {
    async function Busca(): Promise<void> {
      await buscaConvenios();
    }
    if (clinica.CliAtendeConvenio) Busca();
  }, [buscaConvenios, clinica.CliAtendeConvenio]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        setDadosRelatorio(undefined);
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().required(),
          PerCodigo: Yup.string().nullable(true),
          ConUsuCodigo: Yup.string().nullable(true),
          ProCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setClinicaSelecionada(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        let parametrosOpcionais = dados.ConUsuCodigo
          ? `&ConUsuCodigo=${dados.ConUsuCodigo}`
          : '';
        parametrosOpcionais += dados.PerCodigo
          ? `&PerCodigo=${dados.PerCodigo}`
          : '';
        parametrosOpcionais += dados.ProCodigo
          ? `&ProCodigo=${dados.ProCodigo}`
          : '';

        const response = await api.get<IRelatorio[]>(
          `/relatorios/procedimentosrealizadosvalor?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&CliCodigo=${dados.CliCodigo}${parametrosOpcionais}`,
        );

        let totalGeral = 0;
        let totalGeralDesconto = 0;
        let totalGeralPago = 0;

        const dadosFormatados = response.data.map(rel => {
          const convenio = convenios.find(
            con => con.ConvCodigo === rel.AgeConvCodigo,
          );
          return {
            ...rel,
            AgeInicioFormatado: format(
              parseISO(rel.AgeInicio.toString()),
              'dd/MM/yyyy',
            ),
            AgeConvNome:
              convenio?.ConvNome || (rel.AgePlano ? 'ASSOCIADO' : 'PARTICULAR'),
            ProValorFormatado: formatPrice(rel.ProValor)
              .replace(/\s/g, '')
              .replace('R$', ''),
            CprValorFormatado: formatPrice(rel.CprValor),
            CprValorPagFormatado: formatPrice(rel.CprValorPag),
          };
        });

        const ordenado = arrayGroupBy(dadosFormatados, i => i.ConNome);

        const ordenadoFormatado = ordenado.map(ord => {
          const totalPorAgendamento = Array.from(
            new Set(ord.map(dado => dado.AgeCodigo)),
          ).map(AgeCodigo => {
            return {
              AgeCodigo,
              CprValorPag: ord.find(dad => dad.AgeCodigo === AgeCodigo)
                ?.CprValorPag,
            };
          });

          const totalPago = totalPorAgendamento.reduce((tot, item) => {
            return tot + Number(item.CprValorPag);
          }, 0);

          totalGeralPago += totalPago;

          const total = ord.reduce((tot, item) => {
            return tot + Number(item.ProValor);
          }, 0);
          totalGeral += total;

          const totalDesconto = total - totalPago;
          totalGeralDesconto += totalDesconto;

          return {
            grupo: ord[0].ConNome,
            totalDesconto,
            totalDescontoFormatado: formatPrice(totalDesconto),
            total,
            totalFormatado: formatPrice(total),
            totalPago,
            totalPagoFormatado: formatPrice(totalPago),
            dados: ord,
          };
        });

        setDadosRelatorio(ordenadoFormatado);
        setTotais({
          totalFormatado: formatPrice(totalGeral),
          totalPagoFormatado: formatPrice(totalGeralPago),
          totalDescontoFormatado: formatPrice(totalGeralDesconto),
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica, convenios],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const handleDownload = useCallback(async () => {
    setLoading(true);
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        dataInicio: Yup.date().required(),
        dataFim: Yup.date().required(),
        CliCodigo: Yup.string().required(),
        PerCodigo: Yup.string().nullable(true),
        ConUsuCodigo: Yup.string().nullable(true),
        ProCodigo: Yup.string().nullable(true),
      });

      const dados: IFormBusca = {
        dataInicio: formRefBusca.current?.getFieldValue('dataInicio'),
        dataFim: formRefBusca.current?.getFieldValue('dataFim'),
        CliCodigo: formRefBusca.current?.getFieldValue('CliCodigo'),
        PerCodigo: formRefBusca.current?.getFieldValue('PerCodigo'),
        ProCodigo: formRefBusca.current?.getFieldValue('ProCodigo'),
        ConUsuCodigo: formRefBusca.current?.getFieldValue('ConUsuCodigo'),
      };

      await schema.validate(dados, {
        abortEarly: false,
      });

      const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
      const dataFimSelecionada = parseISO(dados.dataFim.toString());

      const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
      const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

      let parametrosOpcionais = dados.ConUsuCodigo
        ? `&ConUsuCodigo=${dados.ConUsuCodigo}`
        : '';
      parametrosOpcionais += dados.PerCodigo
        ? `&PerCodigo=${dados.PerCodigo}`
        : '';
      parametrosOpcionais += dados.ProCodigo
        ? `&ProCodigo=${dados.ProCodigo}`
        : '';

      const response = await api.get(
        `/relatorios/procedimentosrealizadosvalorexport?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&CliCodigo=${dados.CliCodigo}${parametrosOpcionais}`,
      );

      // let headerLine = response.headers['content-disposition'];
      // let startFileNameIndex = headerLine.indexOf('"') + 1
      // let endFileNameIndex = headerLine.lastIndexOf('"')
      // let filename = headerLine.substring(startFileNameIndex, endFileNameIndex)

      const link = document.createElement('a');
      link.href = response.data.url;
      link.setAttribute(
        'download',
        `procedimentoValor${Math.floor(Date.now() / 1000)}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      } else {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Procedimentos Realizados Valor</h2>
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.SUPERVISOR}
            />
          </div>

          {optionsClinica.length > 0 && user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={2}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={2}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={2}>
                  <Select
                    name="CliCodigo"
                    label="Clinica"
                    options={optionsClinica}
                    disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                  />
                </Col>
                <div className="w-100 mt-2" />
                <Col md={3}>
                  <SelectSync
                    name="PerCodigo"
                    label="Perfil"
                    options={optionPerfil}
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>
                <Col md={3}>
                  <SelectSync
                    name="ConUsuCodigo"
                    label="Médico"
                    options={optionsMedico}
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>
                <Col md={3}>
                  <SelectAsync
                    urlApi="procedimentos/combo?termoBusca="
                    name="ProCodigo"
                    label="Procedimento"
                    placeholder="Procurar"
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>

                <Col md={1}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
                <Col md={1}>
                  <br />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-success"
                    disabled={!dadosRelatorio}
                    onClick={() => handleDownload()}
                  >
                    <FaFileDownload />
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`PROCEDIMENTOS REALIZADOS VALOR ${
            clinicaSelecionada.length > 0 ? `- ${clinicaSelecionada} -` : '-'
          } ${dataExibe?.dataDiaSemana || ''}`}
        />

        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '80px' }}>Data</th>
                    <th>Paciente</th>
                    {clinica.CliExibeConvenioMedico && (
                      <th style={{ width: '150px' }}>Convênio</th>
                    )}
                    <th>Procedimento</th>
                    <th style={{ minWidth: '115px' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <React.Fragment key={`pro-${rel.grupo}`}>
                        <tr
                          style={{
                            fontSize: 'Small',
                            height: '30px',
                            fontWeight: 'bold',
                          }}
                        >
                          <td
                            colSpan={
                              // eslint-disable-next-line no-nested-ternary
                              exibeTotais
                                ? clinica.CliExibeConvenioMedico
                                  ? 3
                                  : 2
                                : 4
                            }
                          >
                            <div className="d-flex justify-content-between">
                              {rel.grupo}{' '}
                              <span>Nº Registros: {rel.dados.length}</span>
                            </div>
                          </td>
                          {exibeTotais && (
                            <>
                              <td>Total: {rel.totalFormatado}</td>
                              <td>Total Pago: {rel.totalPagoFormatado}</td>
                            </>
                          )}
                        </tr>
                        {rel.dados.map(relatorio => (
                          <tr
                            key={`proced-${rel.grupo}-${relatorio.AgeCodigo}-${relatorio.ProCodigo}`}
                            data-id={relatorio.AgeCodigo}
                            style={{ fontSize: '.7rem', height: '30px' }}
                          >
                            <td>{relatorio.AgeInicioFormatado}</td>
                            <td>{relatorio.PacNome}</td>
                            {clinica.CliExibeConvenioMedico && (
                              <td>{relatorio.AgeConvNome}</td>
                            )}
                            <td>{relatorio.Procedimento}</td>
                            <td>{relatorio.ProValorFormatado}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  {exibeTotais && (
                    <tr
                      key="fdfdsfsdfdf"
                      style={{
                        fontSize: 'Small',
                        height: '30px',
                        fontWeight: 'bold',
                      }}
                    >
                      <td
                        colSpan={
                          // eslint-disable-next-line no-nested-ternary
                          exibeTotais
                            ? clinica.CliExibeConvenioMedico
                              ? 3
                              : 2
                            : 4
                        }
                        style={{ textAlign: 'right' }}
                      >
                        Total Geral: {totais?.totalFormatado}
                      </td>
                      {/* <td>Total Desc: {totais?.totalDescontoFormatado}</td> */}
                      <td colSpan={2} style={{ textAlign: 'right' }}>
                        Total Pago: {totais?.totalPagoFormatado}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default FechamentoDia;
